import {
  activityLabel,
  activityTypeLabel,
  type ContactRoleInput,
  type ContactUpdate,
  formatBerlinDate,
  formatBerlinTime,
  invoicePaymentState,
  matchesInvoiceListFilter,
  type Note,
  noteStatuses,
  occupiesSlot,
  toBerlinDate,
} from '@praxi/shared'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router'
import { Pencil, Plus, ShieldCheck } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { ActivityFilterBar } from '@/components/activity-filter-bar'
import { ActivityList } from '@/components/activity-list'
import { FilterChips } from '@/components/chip'
import { ContactForm } from '@/components/contact-form'
import { ContactHeader } from '@/components/contact-header'
import { ContactOverview } from '@/components/contact-overview'
import { ContentWidth } from '@/components/content-width'
import { InvoiceFilterBar, useInvoiceColumns } from '@/components/invoice-filter-bar'
import { InvoiceList } from '@/components/invoice-list'
import { NoteChainDialog } from '@/components/note-chain-dialog'
import { NoteForm } from '@/components/note-form'
import { NoteLeaveDialog } from '@/components/note-leave-dialog'
import { NotePanel, NoteReader, orderNotes } from '@/components/note-panel'
import { RecordTab, RecordTabsList } from '@/components/record-tabs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import {
  activitySummaryQueryOptions,
  pastActivitiesQueryOptions,
  upcomingActivitiesQueryOptions,
} from '@/lib/activities'
import {
  type ActivityFilterValue,
  activityFilterSearchSchema,
  activityListParams,
  activitySummaryParams,
} from '@/lib/activity-filters'
import { activityTypeListQueryOptions } from '@/lib/activity-types'
import { ApiError } from '@/lib/api'
import {
  contactAppointmentsQueryOptions,
  contactQueryOptions,
  setContactArchived,
  setContactRoles,
  updateContact,
} from '@/lib/contacts'
import { type InvoiceFilterValue, invoiceFilterSearchSchema } from '@/lib/invoice-filters'
import { invoiceListQueryOptions, invoiceSummaryQueryOptions } from '@/lib/invoices'
import { leaveQuestion, requestLeave } from '@/lib/note-leave'
import { noteTypeListQueryOptions } from '@/lib/note-types'
import { createNote, noteListQueryOptions } from '@/lib/notes'
import { strings } from '@/lib/strings'
import { cn } from '@/lib/utils'

/**
 * The tab lives in the URL so a record can be linked to on the tab that
 * matters — a note to self, or a jump from the invoice list straight into the
 * documentation — and so the back button returns to where one was.
 */
const tabs = ['overview', 'master', 'notes', 'activities', 'appointments', 'invoices'] as const

const searchSchema = z.object({
  tab: z.enum(tabs).default('overview'),
  /**
   * The Vorgang this page was opened for, and what that means is the tab's
   * business: the Vorgänge tab opens it in read mode ("Letzte Vorgänge" on
   * the overview, L5), the Notizen tab starts a note about it
   * ("Dokumentieren", L6b). One parameter, because it is one fact — which
   * Vorgang is in question — and it is in the URL for the same reason `tab`
   * is: a link has to survive the back button.
   */
  activityId: z.uuid().optional(),
  /**
   * What the Vorgänge tab is narrowed to, out of the same schema the Vorgänge
   * page validates its own address with (B2). Here rather than in `useState`
   * so a narrowed view of one record is a link — the tab is in the URL for
   * exactly that reason, and the filter beside it costs nothing.
   *
   * Switching tabs drops it along with `activityId`: it belongs to the tab it
   * narrows.
   */
  ...activityFilterSearchSchema.shape,
  /**
   * What the Rechnungen tab is narrowed to and which invoice is expanded, out
   * of the same schema the Zahlungen page validates its own address with (B3).
   * Here for the reason the Vorgänge filter is: a narrowed view of one record
   * is a link, and the overview points at the draft it just started.
   */
  ...invoiceFilterSearchSchema.shape,
})

export const Route = createFileRoute('/_app/contacts/$contactId')({
  validateSearch: searchSchema,
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(contactQueryOptions(params.contactId)),
  component: ContactDetailPage,
})

function ContactDetailPage() {
  const { contactId } = Route.useParams()
  const search = Route.useSearch()
  const { tab, activityId } = search
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const { data: contact } = useQuery(contactQueryOptions(contactId))

  /** Master data is read far more often than it is changed, so the page starts
   *  read-only and a stray keystroke cannot land in a field nobody opened. */
  const [editing, setEditing] = useState(false)
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['contacts'] })

  /**
   * Two requests, in this order and never the other way round: the roles are
   * their own resource and deliberately cannot travel in the update payload
   * (see `contactUpdateSchema`).
   *
   * If the second one fails the first has already been written, and the screen
   * says exactly that — the master data is saved, the roles are not — and
   * **stays in edit mode** with the ticks as they were typed, so trying again
   * is pressing "Speichern" again. Silently reporting success would leave the
   * record disagreeing with the screen; dropping back to read mode would throw
   * away the only copy of what was meant.
   */
  const save = useMutation({
    mutationFn: async ({ input, roles }: { input: ContactUpdate; roles: ContactRoleInput[] }) => {
      const saved = await updateContact(contactId, input)
      return await setContactRoles(contactId, roles)
        .then((withRoles) => ({ contact: withRoles, rolesFailed: false }))
        .catch(() => ({ contact: saved, rolesFailed: true }))
    },
    onSuccess: async ({ contact: saved, rolesFailed }) => {
      queryClient.setQueryData(contactQueryOptions(contactId).queryKey, saved)
      await invalidate()
      if (rolesFailed) {
        toast.error(strings.contact.rolesSaveFailed)
        return
      }
      setEditing(false)
      toast.success(strings.contact.saved)
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : strings.contact.saveFailed)
    },
  })

  const archive = useMutation({
    mutationFn: (archived: boolean) => setContactArchived(contactId, archived),
    onSuccess: async (saved) => {
      queryClient.setQueryData(contactQueryOptions(contactId).queryKey, saved)
      await invalidate()
      toast.success(saved.archivedAt ? strings.contact.archived : strings.contact.unarchived)
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : strings.error.generic)
    },
  })

  if (!contact) return <p className="text-muted-foreground text-sm">{strings.status.loading}</p>

  const isArchived = Boolean(contact.archivedAt)

  return (
    <Tabs
      value={tab}
      /* The Notizen tab works like a messenger (N7): the record takes the
         height of `main` and never scrolls, the panel fills what the header
         and the filter row leave, and only its two columns scroll. Every other
         tab scrolls the record in `main` under the sticky header, as before. */
      className={tab === 'notes' ? 'h-full min-h-0' : undefined}
      /* The deep-linked Vorgang goes with the tab it belongs to: leaving and
         coming back would otherwise re-open a row nobody clicked. */
      onValueChange={(next) => void navigate({ search: { tab: next as (typeof tabs)[number] } })}
    >
      {/* The header strip runs the full width and carries the tab row, so the
          rule under the tabs spans the whole field. The page itself therefore
          gets no padding from the shell — see `lib/page-chrome.ts`. */}
      <ContactHeader
        contact={contact}
        actions={
          isArchived ? (
            <Button
              variant="ghost"
              className="text-muted-foreground"
              onClick={() => archive.mutate(false)}
              disabled={archive.isPending}
            >
              {strings.contact.unarchive}
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  className="text-muted-foreground"
                  disabled={archive.isPending}
                >
                  {strings.contact.archive}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{strings.contact.archiveTitle}</AlertDialogTitle>
                  <AlertDialogDescription>{strings.contact.archiveBody}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{strings.contact.cancel}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => archive.mutate(true)}>
                    {strings.contact.archive}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )
        }
      >
        <RecordTabsList>
          <RecordTab value="overview">{strings.contact.tabs.overview}</RecordTab>
          <RecordTab value="master">{strings.contact.tabs.master}</RecordTab>
          <RecordTab value="notes">{strings.contact.tabs.notes}</RecordTab>
          <RecordTab value="activities">{strings.contact.tabs.activities}</RecordTab>
          <RecordTab value="appointments">{strings.contact.tabs.appointments}</RecordTab>
          <RecordTab value="invoices">{strings.contact.tabs.invoices}</RecordTab>
        </RecordTabsList>
      </ContactHeader>

      {/* The strip above runs to the edge; below it the cap is per tab, because
          the design does not treat them alike (L5). The Übersicht spreads its
          six cards over the whole field — measured at 268..1696 in a 1728 px
          window, which is the page inset on both sides and nothing else —
          while every other tab is a column of text and stops at 1180. */}
      <div
        className={cn('px-8 pt-6', tab === 'notes' ? 'flex min-h-0 flex-1 flex-col pb-6' : 'pb-11')}
      >
        <TabsContent value="overview">
          <ContactOverview
            contact={contact}
            onDocument={(activity) =>
              void navigate({ search: { tab: 'notes', activityId: activity.id } })
            }
            onOpenActivity={(activityId) =>
              void navigate({ search: { tab: 'activities', activityId } })
            }
          />
        </TabsContent>

        <TabsContent value="master">
          <ContentWidth>
            {!editing && (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-4">
                <p className="text-[13.5px] text-muted-foreground">
                  {strings.contact.masterReadHint}
                </p>
                <Button size="sm" onClick={() => setEditing(true)}>
                  <Pencil className="size-3.5" aria-hidden />
                  {strings.actions.edit}
                </Button>
              </div>
            )}

            {/* `key` remounts the form when the stored version changes or when
                editing is left, so the fields show what was actually stored —
                that is also what "Abbrechen" relies on. */}
            <ContactForm
              key={`${contact.id}${contact.archivedAt ?? ''}${editing}`}
              contact={contact}
              editing={editing}
              onSubmit={(input, roles) => save.mutate({ input, roles })}
              onCancel={() => setEditing(false)}
              pending={save.isPending}
            />
          </ContentWidth>
        </TabsContent>

        <TabsContent value="activities">
          <ContentWidth>
            <ContactActivities
              contactId={contactId}
              openActivityId={activityId}
              filter={search}
              onFilterChange={(change) =>
                void navigate({ search: (previous) => ({ ...previous, ...change }) })
              }
            />
          </ContentWidth>
        </TabsContent>

        <TabsContent value="appointments">
          <ContentWidth>
            <ContactAppointments contactId={contactId} />
          </ContentWidth>
        </TabsContent>

        <TabsContent value="notes" className="flex min-h-0 flex-col">
          <ContentWidth className="flex min-h-0 flex-1 flex-col">
            <ContactNotes
              contactId={contactId}
              documentActivityId={tab === 'notes' ? activityId : undefined}
              onFormOpened={() => void navigate({ search: { tab: 'notes' }, replace: true })}
            />
          </ContentWidth>
        </TabsContent>

        <TabsContent value="invoices">
          <ContentWidth>
            <ContactInvoices
              contactId={contactId}
              filter={search}
              onFilterChange={(change) =>
                void navigate({ search: (previous) => ({ ...previous, ...change }) })
              }
            />
          </ContentWidth>
        </TabsContent>
      </div>
    </Tabs>
  )
}

/**
 * The contact's activities — **the same list, the same filter bar and the same
 * inline detail as the Vorgänge page** (D8, unified in B2). Two things differ,
 * and both follow from the one fact that the contact is fixed here: the name is
 * left out of every row and out of the detail's rail, where it would repeat and
 * lead back to this page, and the create form takes the contact rather than
 * asking for it.
 */
function ContactActivities({
  contactId,
  openActivityId,
  filter,
  onFilterChange,
}: {
  contactId: string
  /** Arrived here from "Letzte Vorgänge" on the overview (L5) — that Vorgang
   *  opens in read mode. */
  openActivityId: string | undefined
  /** In the URL beside `tab`, so a narrowed view of this record is a link. */
  filter: ActivityFilterValue
  onFilterChange: (change: Partial<ActivityFilterValue>) => void
}) {
  /**
   * **The chips narrow on the server and count on the server** (L7). They
   * counted and filtered what had been loaded until then, which was right for
   * exactly as long as one request returned everything: with the past paged to
   * fifty, "7 Geplant" was the number in the first page and not the number the
   * contact has, and picking the chip produced whatever of those fifty
   * matched.
   *
   * Two things follow, and they are why this needs three queries rather than
   * one. The filter travels into both halves of the list, because both are
   * narrowed the same way; the **counts do not**, because a chip's number has
   * to hold still when the chip is pressed — see `activitySummaryParams`.
   */
  const params = activityListParams(contactId, filter)
  const upcoming = useQuery(upcomingActivitiesQueryOptions(params))
  const past = useInfiniteQuery(pastActivitiesQueryOptions(params))
  const counts = useQuery(activitySummaryQueryOptions(activitySummaryParams(contactId, filter)))
  const [creating, setCreating] = useState(false)

  const summary = counts.data

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <ActivityFilterBar
          value={filter}
          onChange={onFilterChange}
          summary={summary}
          summaryText={summary && strings.counts.activities(summary.total, summary.upcoming)}
        />
        <Button className="mb-[3px] ml-auto" onClick={() => setCreating(true)}>
          <Plus className="size-4" aria-hidden />
          {strings.activity.create}
        </Button>
      </div>

      <ActivityList
        openActivityId={openActivityId}
        upcoming={upcoming.data ?? []}
        past={past.data?.pages.flatMap((page) => page.items) ?? []}
        emptyText={
          upcoming.isPending || past.isPending ? strings.status.loading : strings.activity.empty
        }
        showContact={false}
        contactId={contactId}
        creating={creating}
        onCreated={() => setCreating(false)}
        onCancelCreate={() => setCreating(false)}
        hasMorePast={past.hasNextPage}
        loadingMorePast={past.isFetchingNextPage}
        onLoadMorePast={() => void past.fetchNextPage()}
      />
    </>
  )
}

/**
 * The contact's invoices — **the same filter bar and the same table as the
 * Zahlungen page** (B3). Not similar: `InvoiceFilterBar` and `InvoiceList`,
 * out of one filter schema, with the same seven columns and the same column
 * preference.
 *
 * It used to be a screen of its own: three chips nobody else had, a
 * hand-written list of rows, a summary sentence of its own wording, and a card
 * of what was still billable. The chips and the rows are gone into the shared
 * pair; the card is gone for a different reason, and it is the design's — open
 * Vorgänge live under Zahlungen and nowhere else. Nothing is lost with it: the
 * position list inside the editor offers everything still open for this
 * contact, ticked or not, which is where one would put it on an invoice
 * anyway (L8).
 *
 * **The "Empfänger" column stays here**, and it is not the surrounding page
 * repeated: since L8 the document may be addressed to somebody else, and which
 * rows those are is exactly what one wants to see.
 *
 * The one difference from the page is inside the editor — `contactId` means
 * the recipient is already known, so "Neue Rechnung" writes the draft at once
 * instead of asking who it is for.
 */
function ContactInvoices({
  contactId,
  filter,
  onFilterChange,
}: {
  contactId: string
  /** In the URL beside `tab=invoices`, so a narrowed view of this record is a
   *  link. */
  filter: InvoiceFilterValue
  onFilterChange: (change: Partial<InvoiceFilterValue>) => void
}) {
  const invoices = useQuery(invoiceListQueryOptions({ contactId }))
  /** The chips count on the server, over every document of this contact —
   *  never over the rows a request returned (B3). */
  const summary = useQuery(invoiceSummaryQueryOptions(contactId))
  const { columns, setColumns } = useInvoiceColumns()
  const [creating, setCreating] = useState(false)

  const today = toBerlinDate(new Date().toISOString())
  const rows = (invoices.data ?? []).filter(
    (invoice) =>
      filter.invoiceFilter === undefined ||
      matchesInvoiceListFilter(
        invoice,
        invoicePaymentState(invoice, invoice.paidCents, today),
        filter.invoiceFilter,
      ),
  )

  return (
    <>
      <InvoiceFilterBar
        className="mb-4"
        summary={summary.data}
        filter={filter.invoiceFilter}
        onFilterChange={(next) => onFilterChange({ invoiceFilter: next })}
        columns={columns}
        onColumnsChange={setColumns}
        action={
          /* The container's own button, as on the page — there it sits in the
             page header, and a contact record has none to sit in. */
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            {strings.invoice.create}
          </Button>
        }
      />

      <InvoiceList
        /**
         * **The card is bounded against the window, so its heading can stick**
         * — the same table as on Zahlungen, and there the page owns the
         * height and the card simply fills what is left.
         *
         * Here it cannot: this record scrolls as a whole, with the header
         * strip sticky inside `main`. A heading anchored to that scrollport
         * would slide under the strip. The number is measured — topbar, strip,
         * the tab's inset, the chip row.
         *
         * What would take it away is this tab taking the record's height the
         * way the Notizen tab does since N7 (`h-full` on `Tabs` above). Left
         * as it is on purpose in N7; the other tabs were not to change.
         */
        className="max-h-[calc(100svh-318px)]"
        invoices={rows}
        columns={columns}
        contactId={contactId}
        creating={creating}
        onCreated={() => setCreating(false)}
        onCancelCreate={() => setCreating(false)}
        openInvoiceId={filter.invoiceId}
        filtered={filter.invoiceFilter !== undefined}
        emptyText={invoices.isPending ? strings.status.loading : strings.invoice.empty}
        emptyFilteredText={strings.invoice.emptyFiltered}
      />
    </>
  )
}

function ContactNotes({
  contactId,
  documentActivityId,
  onFormOpened,
}: {
  contactId: string
  /** An activity handed over by "Dokumentieren" — a note about it is started
   *  once, and the parameter is dropped again so that going back to this tab
   *  later does not start another nobody asked for. */
  documentActivityId?: string | undefined
  onFormOpened: () => void
}) {
  const notes = useQuery(noteListQueryOptions({ contactId }))
  const noteTypes = useQuery(noteTypeListQueryOptions)
  const noteRows = notes.data ?? []
  const [filter, setFilter] = useState<string | undefined>()

  /* The three statuses first, then the addendum, then one chip per type flagged
     `show_as_tab` — the flag alone decides that last group, so a chip appears
     even where the count is zero: at a filter a zero is an answer (K7), and
     which types are worth a chip is the practitioner's call rather than a
     consequence of what this contact happens to have.

     "Nachtrag" stands with the statuses rather than among the types,
     because that is what it is: a property of the note itself. It is not a
     note type and never becomes one (L1) — the design draws it between the
     types, which no general order can reproduce. */
  const noteChips = [
    ...noteStatuses.map((status) => ({
      id: `status:${status}`,
      label: strings.note.status[status],
      matches: (note: Note) => note.status === status,
    })),
    {
      id: 'addendum',
      label: strings.counts.notesAddendum,
      matches: (note: Note) => note.correctsNoteId !== null,
    },
    ...(noteTypes.data ?? [])
      .filter((type) => type.showAsTab)
      /* An addendum is deliberately not counted under its type. It carries
         one — it starts on its parent's — but "Nachtrag" displaces it
         everywhere it is shown, in the row and in the reading pane's header,
         and a count that said otherwise would not add up against the rows. */
      .map((type) => ({
        id: `type:${type.id}`,
        label: type.label,
        matches: (note: Note) => note.correctsNoteId === null && note.noteTypeId === type.id,
      })),
  ]

  /**
   * **Counting and filtering are two rules, not one** (L6b).
   *
   * - **Counted** row by row, on the row's own property: an addendum counts
   *   under "Nachtrag" and under nothing else.
   * - **Filtered** with closure in both directions: a matching note brings its
   *   addenda along, and a matching addendum brings its note along.
   *
   * The rule from L1 survives that unchanged — an addendum never stands alone,
   * because alone in a filtered list it would sit there with nothing to say
   * what it corrects, while the panel renders it indented under exactly that
   * note. What follows is that a chip reading "2 Sitzung" can produce three
   * rows, and that is the lesser evil: a number beside a word reads as "there
   * are this many", not as "this many rows are coming".
   */
  const shownFor = (matches: (note: Note) => boolean): Note[] => {
    const keep = new Set(noteRows.filter(matches).map((note) => note.id))

    // Both directions, to a fixed point — the screen only ever nests one level
    // deep, but nothing in the database says an addendum may not have one.
    for (let pass = 0; pass <= noteRows.length; pass++) {
      let grew = false
      for (const note of noteRows) {
        if (note.correctsNoteId === null) continue
        const linked = keep.has(note.id) !== keep.has(note.correctsNoteId)
        if (!linked) continue
        keep.add(note.id)
        keep.add(note.correctsNoteId)
        grew = true
      }
      if (!grew) break
    }

    return noteRows.filter((note) => keep.has(note.id))
  }

  const active = noteChips.find((chip) => chip.id === filter)
  const shown = active ? shownFor(active.matches) : noteRows
  const noTypes = noteTypes.data?.length === 0

  /** The note being written, by id: the row itself comes from the list, so
   *  a file attached in the form shows up there at once. Until the list has
   *  fetched it, the copy the start answered with stands in. */
  const [editing, setEditing] = useState<Note | null>(null)
  const editingNote = editing ? (noteRows.find((note) => note.id === editing.id) ?? editing) : null
  const [selectedId, setSelectedId] = useState<string | undefined>()
  const [chainOpen, setChainOpen] = useState(false)
  const queryClient = useQueryClient()

  /**
   * "Neue Notiz", "Nachtrag" and "Dokumentieren" start a draft on the server
   * and open the form on it (N1b) — never a form without a note behind it. A
   * failed start opens nothing and says so. The buttons are disabled while
   * one is running, which is what stops a double click from starting two;
   * clicking again afterwards starts another, on purpose.
   */
  const start = useMutation({
    mutationFn: (what: { activityId?: string; correctsNoteId?: string }) =>
      createNote({
        contactId,
        activityId: what.activityId ?? null,
        correctsNoteId: what.correctsNoteId ?? null,
      }),
    onSuccess: async (started) => {
      setEditing(started)
      setSelectedId(started.id)
      await queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : strings.note.startFailed)
    },
  })

  /* "Dokumentieren" arrives as a parameter. The ref is against StrictMode's
     double invocation, which would otherwise start two notes. */
  const documented = useRef<string | null>(null)
  useEffect(() => {
    if (!documentActivityId || documented.current === documentActivityId) return
    documented.current = documentActivityId
    start.mutate({ activityId: documentActivityId })
    onFormOpened()
  }, [documentActivityId, onFormOpened, start.mutate])

  // The first note unless the chosen one is still in the list — which is what
  // makes a filter usable: narrowing the list moves the reading pane with it
  // instead of leaving it on something no longer shown.
  const ordered = orderNotes(shown)
  const selected = ordered.find((note) => note.id === selectedId) ?? ordered[0]

  /**
   * Leaving a note asks first when something would be lost (#39): every way
   * out of this tab through the router — another tab, the sidebar, another
   * contact, back and forward — is held here, and the ways that only change
   * what this tab shows ask before they do. The question is `requestLeave`'s.
   * Never in the way of `/login`: an expired session must not hold anybody,
   * and signing out has asked already. `enableBeforeUnload` stays off — a
   * reload or a closed tab is the browser's question (#38), asked once.
   */
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (next.pathname === '/login') return false
      const stays =
        next.pathname === current.pathname && (next.search as { tab?: string }).tab === 'notes'
      return !stays && leaveQuestion() !== null
    },
    withResolver: true,
    enableBeforeUnload: false,
  })
  useEffect(() => {
    if (blocker.status !== 'blocked') return
    void requestLeave().then((leave) => (leave ? blocker.proceed() : blocker.reset()))
  }, [blocker])

  /** Another note picked in the list. Back to the note being edited, or the
   *  one already read, is no leaving as far as uploads go — they pass over. */
  async function selectNote(noteId: string) {
    const sameNote = (editingNote ?? selected)?.id === noteId
    if (!(await requestLeave({ sameNote }))) return
    setSelectedId(noteId)
    setEditing(null)
  }

  /** "Neue Notiz" and "Nachtrag" replace what is open. */
  async function startNote(what: { correctsNoteId?: string }) {
    if (!(await requestLeave())) return
    start.mutate(what)
  }

  /** A chip narrows the list; the reading pane follows it to another note
   *  only after asking, if uploads run in it. The form stays either way. */
  async function selectFilter(next: string | undefined) {
    if (!editingNote) {
      const chip = noteChips.find((entry) => entry.id === next)
      const rows = orderNotes(chip ? shownFor(chip.matches) : noteRows)
      const nextSelected = rows.find((note) => note.id === selectedId) ?? rows[0]
      if (nextSelected?.id !== selected?.id && !(await requestLeave())) return
    }
    setFilter(next)
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterRow
          summary={strings.counts.notes(noteRows.length)}
          chips={noteChips.map((chip) => ({
            id: chip.id,
            label: chip.label,
            count: noteRows.filter(chip.matches).length,
          }))}
          active={filter}
          onChange={(next) => void selectFilter(next)}
        />
        <Button variant="outline" className="ml-auto" onClick={() => setChainOpen(true)}>
          <ShieldCheck className="size-4" aria-hidden />
          {strings.note.chainCheck}
        </Button>
        {/* Without a note type nothing can be saved, so the button says so
            instead of opening a form that cannot be submitted. */}
        <Button onClick={() => void startNote({})} disabled={noTypes || start.isPending}>
          <Plus className="size-4" aria-hidden />
          {strings.note.create}
        </Button>
      </div>

      {noTypes && (
        <p className="mb-4 text-muted-foreground text-sm">
          <span className="font-medium text-foreground">{strings.note.typesEmpty}</span>{' '}
          {strings.note.typesEmptyHint}
        </p>
      )}

      <NotePanel
        notes={shown}
        selectedId={editingNote ? editingNote.id : selected?.id}
        /* Picking another note asks first when the form has changes or an
           upload runs (#39); with nothing to lose it is one click, as reading
           must be. */
        onSelect={(noteId) => void selectNote(noteId)}
        emptyText={notes.isPending ? strings.status.loading : strings.note.empty}
      >
        {editingNote ? (
          <NoteForm
            /* A different note is a different form: the key is what resets
               every field, the editor included. */
            key={editingNote.id}
            note={editingNote}
            onSaved={(saved) => {
              setEditing(null)
              setSelectedId(saved.id)
            }}
            onClose={() => setEditing(null)}
            onDeleted={() => {
              setEditing(null)
              setSelectedId(undefined)
            }}
          />
        ) : (
          selected && (
            <NoteReader
              note={selected}
              onEdit={(note) => setEditing(note)}
              onAddendum={(note) => void startNote({ correctsNoteId: note.id })}
              starting={start.isPending}
            />
          )
        )}
      </NotePanel>

      <NoteChainDialog contactId={contactId} open={chainOpen} onOpenChange={setChainOpen} />
      <NoteLeaveDialog />
    </>
  )
}

/**
 * The calendar entries of this contact, taken from their activities rather
 * than from a second endpoint — an appointment always belongs to one.
 */
/**
 * The contact's calendar entries — **all** of them (L5).
 *
 * It used to read the Vorgang list and keep the rows that had a Termin, which
 * meant that an appointment belonging to this contact but to no Vorgang — a
 * call back, a slot held for them, possible since D-K1 — was missing from the
 * one screen that claims to list their appointments. The query now asks
 * `appointment` directly and hands back what the calendar sees.
 */
function ContactAppointments({ contactId }: { contactId: string }) {
  const appointments = useQuery(contactAppointmentsQueryOptions(contactId))
  const types = useQuery(activityTypeListQueryOptions(true))

  const entries = appointments.data ?? []

  if (appointments.isPending) {
    return <p className="text-muted-foreground text-sm">{strings.status.loading}</p>
  }

  if (entries.length === 0) {
    return <p className="text-muted-foreground text-sm">{strings.appointment.empty}</p>
  }

  return (
    <ul className="space-y-2">
      {entries.map((entry) => (
        <li
          key={entry.id}
          className="flex flex-wrap items-baseline gap-x-3 rounded-md border px-4 py-3"
        >
          <span className="font-medium">{formatBerlinDate(entry.startsAt)}</span>
          <span className="text-muted-foreground text-sm tabular-nums">
            {formatBerlinTime(entry.startsAt)}–{formatBerlinTime(entry.endsAt)}
          </span>
          <Badge variant={occupiesSlot(entry.status) ? 'outline' : 'secondary'}>
            {strings.appointment.status[entry.status]}
          </Badge>
          <span className="text-muted-foreground text-sm">
            {/* The same two-part naming the calendar uses (D-K2), minus the
                contact — inside their own record it would repeat on every
                row. */}
            {entry.activityTypeId === null
              ? (entry.title ?? strings.appointment.untitled)
              : activityLabel(
                  { title: entry.activityTitle },
                  activityTypeLabel(types.data, entry.activityTypeId),
                )}
          </span>
        </li>
      ))}
    </ul>
  )
}

/**
 * The filter row above a tab's list: a prose summary, then one chip per
 * category — and clicking one narrows the list (K3, corrected in K7).
 *
 * K3 built these as plain counts, on the reading that the prototype's chips
 * were controls without a function. That was a misreading: `nzFilter`,
 * `vgFilter` and `reFilter` narrow the three lists there. So they filter here
 * too — and **a zero stays visible**, because at a filter it is an answer
 * ("Überfällig 0") rather than the noise it would be at a bare count.
 *
 * The active chip is toggled off by clicking it again, as in the prototype.
 * **The count comes first**, as on every filter chip: there the number is the
 * statement — how many rows to expect — while a tab's number is an aside to
 * its name (K8, `components/chip.tsx`).
 */
function FilterRow<Id extends string>({
  summary,
  chips,
  active,
  onChange,
}: {
  summary: string
  chips: { id: Id; label: string; count: number }[]
  active: Id | undefined
  onChange: (next: Id | undefined) => void
}) {
  return (
    /* The pills themselves are `FilterChips`, the one implementation every
       list on this screen and the Vorgänge page share (B2). What is left here
       is the sentence in front of them. */
    <div className="flex flex-wrap items-center gap-2">
      <p className="text-[13.5px] text-muted-foreground">{summary}</p>
      <FilterChips chips={chips} active={active} onChange={onChange} />
    </div>
  )
}
